import { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

/**
 * A yes/no confirmation using the existing back-office design.
 * No browser-native blocking dialogs, and no mutation until consent.
 */
export default function useAdminConfirm() {
  const [pending, setPending] = useState(null);
  const resolveRef = useRef(null);

  useEffect(() => () => {
    // If a page change unmounts this component, a pending action is cancelled.
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(false);
  }, []);

  const askConfirm = useCallback((options) => {
    // Ignore a second click while a decision is already pending.
    if (resolveRef.current) return Promise.resolve(false);
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setPending(options);
    });
  }, []);

  const answer = useCallback((accepted) => {
    const resolve = resolveRef.current;
    if (!resolve) return;
    resolveRef.current = null;
    setPending(null);
    resolve(accepted);
  }, []);

  return {
    askConfirm,
    confirmDialog: (
      <ConfirmDialog
        open={Boolean(pending)}
        title={pending?.title || 'Confirmer cette action'}
        description={pending?.description || ''}
        details={pending?.details}
        confirmLabel={pending?.confirmLabel || 'Confirmer'}
        intent={pending?.intent || 'default'}
        onConfirm={() => answer(true)}
        onCancel={() => answer(false)}
      />
    ),
  };
}
